import { bunja } from 'bunja';
import { atom } from 'jotai';

import { positionBracketBunja } from '@flipster/features/src/shared/position-bracket/state';
import { openPositionsBunja } from '@flipster/r230522/original/position';
import { validateBracketPositionSize } from '@flipster/utils/calculator/bracket-formula';
import { getQuoteCurrencyPrecision } from '@flipster/utils/constants/currency';
import Num, { BigNumber } from '@flipster/utils/num';

import { commonFormBunja } from '../../common';
import { perpAccountBunja, symbolBunja } from './base';
import { formBunja } from './form';

export const bracketBunja = bunja(() => {
  const form = bunja.use(formBunja);
  const { symbolInfoAtom, askPriceAtom, bidPriceAtom } = bunja.use(symbolBunja);
  const { tradeModeAtom, availableFundsAtom } = bunja.use(perpAccountBunja);
  const { openPositionsAtom } = bunja.use(openPositionsBunja);
  const { priceTypeAtom } = bunja.use(commonFormBunja);

  const { positionBracketAtom, positionBracketMultiplierAtom, symbolLevelBracketParamsAtom } =
    bunja.use(positionBracketBunja);

  /**
   * ref1: https://prextech.slack.com/archives/C087D884VEE/p1768470928082629?thread_ts=1768469752.662279&cid=C087D884VEE
   * ref2: https://prextech.slack.com/archives/C07DN09JTUY/p1776245215359439
   *  One-way: Reduce only 이거나 기존 포지션과 반대 방향(감소)이면 bracket 클라이언트 검증 스킵 */
  const canSkipValidateAtom = atom((get) => {
    const tradeMode = get(tradeModeAtom);

    switch (tradeMode) {
      case 'OneWay': {
        if (get(form.reduceOnlyAtom)) return true;

        const { symbol } = get(symbolInfoAtom);
        const side = get(form.sideAtom);
        const openPositions = get(openPositionsAtom)?.[symbol] || [];

        if (side !== 'unknown' && openPositions.length > 0 && openPositions[0]) {
          // one way mode 인 경우에는 같은 심볼의 포지션을 하나 잡을 수 있으므로 0번째 인덱스를 사용한다.
          const existingPosition = openPositions[0];
          return existingPosition.side !== side;
        }
        return false;
      }
      case 'MultiplePositions':
      default:
        return false;
    }
  });

  const bracketParamsAtom = atom((get) => {
    const priceType = get(priceTypeAtom);
    const side = get(form.sideAtom);
    const price = (() => {
      switch (priceType) {
        case 'market':
          // Open position의 position bracket validation는 submit 할 때 검증하므로,
          // 이때는 side를 알 수 있으니 bbo price로 계산한다.
          return side === 'Long' ? get(askPriceAtom) : get(bidPriceAtom);
        default:
          return get(form.calculationPriceAtom) || 0;
      }
    })();

    const { totalEffectiveLeverage, existingPositionSize, existingMargin } = get(symbolLevelBracketParamsAtom);

    const availableFunds = Num(get(availableFundsAtom) || 0);

    return {
      price,
      totalEffectiveLeverage,
      existingPositionSize,
      existingMargin,
      availableFunds,
    };
  });

  const bracketValidationAtom = atom((get) => {
    const canSkip = get(canSkipValidateAtom);
    const orderState = get(form.orderStateAtom);
    const positionBracket = get(positionBracketAtom);
    const reduceOnly = get(form.reduceOnlyAtom);
    const { quoteCurrency, tradeQtyPrecision } = get(symbolInfoAtom);
    const positionBracketMultiplier = get(positionBracketMultiplierAtom);
    const tradeMode = get(tradeModeAtom);

    const quoteCurrencyPrecision = getQuoteCurrencyPrecision(quoteCurrency);
    const { existingPositionSize, existingMargin, availableFunds, price } = get(bracketParamsAtom);

    const newLeverage = orderState.leverage;
    const availableFundsPositionSize = availableFunds.multipliedBy(newLeverage);

    let isValid = false;
    let maxAvailableMargin = availableFunds;
    let maxAvailablePositionSize = availableFundsPositionSize;
    let maxAvailableQty = availableFundsPositionSize
      .dividedBy(price)
      .decimalPlaces(tradeQtyPrecision, BigNumber.ROUND_DOWN);

    if (canSkip) {
      return {
        isValid: true,
        maxAvailableMargin,
        maxAvailablePositionSize,
        maxAvailableQty,
      };
    }

    if (!positionBracket || !tradeMode) {
      return {
        isValid,
        maxAvailableMargin,
        maxAvailablePositionSize,
        maxAvailableQty,
      };
    }

    switch (orderState.kind) {
      case 'margin': {
        const newLeverage = orderState.leverage;
        const newMargin = Num(orderState.margin || 0).multipliedBy(reduceOnly ? -1 : 1);
        const newPositionSize = newMargin.multipliedBy(newLeverage);

        const { isValid: isBracketValid, maxAvailableMargin: maxBracketAvailableMargin } = validateBracketPositionSize({
          tradeMode,
          symbolLevelParams: {
            existingPositionSize,
            newPositionSize,
            existingMargin,
            newLeverage,
          },
          positionLevelParams: {
            currentPositionSize: Num(0),
            newPositionSize,
            currentMargin: Num(0),
            newMargin,
          },
          positionBracket,
          positionBracketMultiplier,
          options: {
            availableMargin: tradeMode === 'OneWay' && reduceOnly ? Num(existingMargin) : Num(availableFunds),
          },
        });

        isValid = isBracketValid;
        maxAvailableMargin = BigNumber.min(
          maxBracketAvailableMargin,
          tradeMode === 'OneWay' && reduceOnly ? existingMargin : availableFunds,
        ).decimalPlaces(quoteCurrencyPrecision, BigNumber.ROUND_DOWN);
        break;
      }
      case 'quantity': {
        const newQty = Num(orderState.quantity || 0);

        const newPositionSize = newQty.multipliedBy(price).multipliedBy(reduceOnly ? -1 : 1);
        const newMargin = newPositionSize.dividedBy(newLeverage);

        const { isValid: isBracketValid, maxAvailablePositionSize: maxBracketAvailablePositionSize } =
          validateBracketPositionSize({
            tradeMode,
            symbolLevelParams: {
              existingPositionSize,
              newPositionSize,
              existingMargin,
              newLeverage,
            },
            positionLevelParams: {
              currentPositionSize: Num(0),
              newPositionSize,
              currentMargin: Num(0),
              newMargin,
            },
            positionBracket,
            positionBracketMultiplier,
            options: {
              availableMargin:
                tradeMode === 'OneWay' && reduceOnly ? Num(existingPositionSize) : Num(availableFundsPositionSize),
            },
          });

        isValid = isBracketValid;
        maxAvailablePositionSize = BigNumber.min(
          maxBracketAvailablePositionSize,
          tradeMode === 'OneWay' && reduceOnly ? existingPositionSize : availableFundsPositionSize,
        );
        maxAvailableQty = maxAvailablePositionSize
          .dividedBy(price)
          .decimalPlaces(tradeQtyPrecision, BigNumber.ROUND_DOWN);
        break;
      }
      case 'amount': {
        const newLeverage = orderState.leverage;
        const newPositionSize = Num(orderState.amount || 0).multipliedBy(reduceOnly ? -1 : 1);
        const newMargin = newPositionSize.dividedBy(newLeverage);

        const { isValid: isValidBracket, maxAvailablePositionSize: maxBracketAvailablePositionSize } =
          validateBracketPositionSize({
            tradeMode,
            symbolLevelParams: {
              existingPositionSize,
              newPositionSize,
              existingMargin,
              newLeverage,
            },
            positionLevelParams: {
              currentPositionSize: Num(0),
              newPositionSize,
              currentMargin: Num(0),
              newMargin,
            },
            positionBracket,
            positionBracketMultiplier,
            options: {
              availableMargin:
                tradeMode === 'OneWay' && reduceOnly ? Num(existingPositionSize) : Num(availableFundsPositionSize),
            },
          });

        isValid = isValidBracket;
        maxAvailablePositionSize = BigNumber.min(
          maxBracketAvailablePositionSize,
          tradeMode === 'OneWay' && reduceOnly ? existingPositionSize : availableFundsPositionSize,
        ).decimalPlaces(quoteCurrencyPrecision, BigNumber.ROUND_DOWN);
        break;
      }
    }

    return {
      isValid,
      maxAvailableMargin,
      maxAvailablePositionSize,
      maxAvailableQty,
    };
  });

  return {
    bracketValidationAtom,
  };
});
bracketBunja.debugLabel = 'bracketBunja';
